import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { supabase } from '../lib/supabase';
import { createOrUpdateApplication } from '../lib/api/applications';
import {
  getAttendeeProfile,
  createOrUpdateAttendeeProfile,
  createOrUpdateChurchInfo,
} from '../lib/api/profiles';
import type { EventSignupOption, SignupOptionMode } from '../types/database';
import ApplicationConfirmation, { type CoordinatorContact } from './ApplicationConfirmation';
import ChurchNameInput from './ChurchNameInput';
import './MissionApplicationForm.css';
import { captureError } from '../lib/capture-error';
import { formatSignupOptionSchedule } from '../lib/dates';
import { phoneToProfileFields, resolveProfilePhone } from '../lib/event-application';
import { callEdgeFunction } from '../lib/call-edge-function';
import { buildEventApplyPath, formatSignupPrice, hasSignupPrice } from '../lib/signup-options';

interface MinimalFormData {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  churchName: string;
}

interface MinimalEventApplicationFormProps {
  eventId: string;
  eventSlug?: string;
  eventTitle?: string;
  coordinatorContact?: CoordinatorContact | null;
  signupOptions?: EventSignupOption[];
  signupOptionMode?: SignupOptionMode;
  initialSelectedOptions?: string[];
}

const MinimalEventApplicationForm: React.FC<MinimalEventApplicationFormProps> = ({
  eventId,
  eventSlug,
  eventTitle = 'Event Registration',
  coordinatorContact = null,
  signupOptions = [],
  signupOptionMode = 'single',
  initialSelectedOptions = [],
}) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [formData, setFormData] = useState<MinimalFormData>({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    churchName: '',
  });
  const [selectedSignupOptions, setSelectedSignupOptions] = useState<string[]>(initialSelectedOptions);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedApplicationId, setSubmittedApplicationId] = useState<string | null>(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showAlreadyApplied, setShowAlreadyApplied] = useState(false);
  const [isNewAccount, setIsNewAccount] = useState(false);
  const [showFinishProfile, setShowFinishProfile] = useState(false);

  const signInRedirectPath = useMemo(() => {
    if (selectedSignupOptions.length === 1) {
      return buildEventApplyPath(eventSlug || eventId, selectedSignupOptions[0]);
    }
    return buildEventApplyPath(eventSlug || eventId);
  }, [eventSlug, eventId, selectedSignupOptions]);

  const selectedOptionsTotal = useMemo(
    () =>
      signupOptions
        .filter((o) => selectedSignupOptions.includes(o.id))
        .reduce((sum, o) => sum + (o.price ?? 0), 0),
    [signupOptions, selectedSignupOptions]
  );

  const showSelectedOptionsTotal = selectedOptionsTotal > 0;

  useEffect(() => {
    const loadProfile = async () => {
      if (!user) return;
      try {
        const profile = await getAttendeeProfile(user.id);
        if (!profile) return;
        setFormData((prev) => ({
          ...prev,
          firstName: profile.first_name || prev.firstName,
          lastName: profile.last_name || prev.lastName,
          phone: resolveProfilePhone(profile) || prev.phone,
          email: profile.email || user.email || prev.email,
          churchName: profile.church_info?.church_name || prev.churchName,
        }));
      } catch (err) {
        captureError(err, 'Error loading profile for minimal application');
      }
    };
    loadProfile();
  }, [user]);

  const validate = (): Record<string, string> => {
    const newErrors: Record<string, string> = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.churchName.trim()) newErrors.churchName = 'Church name is required';
    if (signupOptions.length > 0 && selectedSignupOptions.length === 0) {
      newErrors.signupOptions = 'Please select a registration option';
    }
    return newErrors;
  };

  const handleSignupOptionToggle = (optionId: string) => {
    if (signupOptionMode === 'single') {
      setSelectedSignupOptions([optionId]);
    } else {
      setSelectedSignupOptions((prev) =>
        prev.includes(optionId) ? prev.filter((id) => id !== optionId) : [...prev, optionId]
      );
    }
    if (errors.signupOptions) {
      setErrors((prev) => {
        const { signupOptions: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const saveLoggedInApplication = async (userId: string, action: 'draft' | 'submit') => {
    const phoneFields = phoneToProfileFields(formData.phone.trim());
    const profile = await createOrUpdateAttendeeProfile(userId, {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      ...phoneFields,
    });

    if (!profile.id) return null;

    await createOrUpdateChurchInfo(profile.id, {
      church_name: formData.churchName.trim(),
    });

    const application = await createOrUpdateApplication(
      eventId,
      profile.id,
      {},
      action === 'submit' ? 'submitted' : 'draft',
      selectedSignupOptions.length > 0 ? selectedSignupOptions : null
    );

    const fullProfile = await getAttendeeProfile(userId);
    setShowFinishProfile(!fullProfile?.profile_completed);
    return application.id;
  };

  const createAccountAndSave = async (action: 'draft' | 'submit') => {
    if (!formData.email.trim()) {
      setErrors((prev) => ({ ...prev, email: 'Email is required to save your application' }));
      return;
    }

    setIsSubmitting(true);
    try {
      const randomBytes = new Uint8Array(32);
      crypto.getRandomValues(randomBytes);
      const tempPassword = Array.from(randomBytes, (b) => b.toString(16).padStart(2, '0')).join('');
      const eventPath = eventSlug?.trim() || eventId;
      const redirectUrl = `${window.location.origin}/events/${encodeURIComponent(eventPath)}/apply`;

      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: tempPassword,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: formData.firstName,
            last_name: formData.lastName,
          },
        },
      });

      if (error) {
        if (error.message.includes('rate limit') || (error as { code?: string }).code === 'over_email_send_rate_limit') {
          throw new Error('Too many attempts. Please wait a few minutes before trying again.');
        }
        if (error.message.includes('already registered') || error.message.includes('already exists')) {
          setShowLoginPrompt(true);
          return;
        }
        throw error;
      }

      if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
        setShowLoginPrompt(true);
        return;
      }

      if (data.user) {
        const phoneFields = phoneToProfileFields(formData.phone.trim());

        if (data.session) {
          const appId = await saveLoggedInApplication(data.user.id, action);
          setSubmittedApplicationId(appId);
        } else {
          const { data: submitData, error: submitError } = await callEdgeFunction('submit-application', {
            eventId,
            action,
            profile: {
              first_name: formData.firstName,
              last_name: formData.lastName,
              email: formData.email,
              ...phoneFields,
              date_of_birth: '',
            },
            church_info: {
              church_name: formData.churchName.trim(),
            },
            selected_signup_options: selectedSignupOptions,
          });

          if (submitError || submitData?.error) {
            const errorCode = submitError?.message || String(submitData?.error || '');
            if (errorCode === 'already_registered') {
              setShowLoginPrompt(true);
              return;
            }
            if (errorCode === 'already_applied') {
              setShowAlreadyApplied(true);
              return;
            }
            throw submitError || new Error(String(submitData?.error || 'Failed to save application'));
          }

          setSubmittedApplicationId(String(submitData?.applicationId || ''));
          setShowFinishProfile(true);
        }

        setIsNewAccount(true);
        if (action === 'submit') {
          setIsSubmitted(true);
        } else {
          showToast('Registration saved successfully!', 'success');
        }
      }
    } catch (err: unknown) {
      captureError(err, 'Error saving minimal application');
      const message = err instanceof Error ? err.message : 'Failed to save application. Please try again.';
      setSaveError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const performSave = async (action: 'draft' | 'submit') => {
    setSaveError(null);
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      if (action === 'submit') {
        showToast('Please complete all required fields before submitting.', 'error');
      }
      return;
    }

    if (!user) {
      await createAccountAndSave(action);
      return;
    }

    setIsSubmitting(true);
    try {
      const appId = await saveLoggedInApplication(user.id, action);
      setSubmittedApplicationId(appId);
      if (action === 'submit') {
        setIsSubmitted(true);
      } else {
        showToast('Registration saved successfully!', 'success');
      }
    } catch (err: unknown) {
      captureError(err, action === 'submit' ? 'Error submitting minimal application' : 'Error saving draft');
      setSaveError(err instanceof Error ? err.message : 'Failed to save. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <ApplicationConfirmation
        applicationId={submittedApplicationId || undefined}
        eventTitle={eventTitle}
        applicantName={`${formData.firstName} ${formData.lastName}`}
        applicantEmail={formData.email}
        submissionDate={new Date().toISOString()}
        isNewAccount={isNewAccount}
        coordinatorContact={coordinatorContact ?? undefined}
        showFinishProfile={showFinishProfile}
      />
    );
  }

  if (showLoginPrompt) {
    return (
      <div className="mission-application-form">
        <div className="login-prompt-container">
          <div className="card" style={{ maxWidth: '500px', margin: '0 auto', padding: '2rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>Account Already Exists</h3>
            <p style={{ marginBottom: '1.5rem', color: '#666' }}>
              An account with the email <strong>{formData.email}</strong> already exists.
              Please sign in to continue.
            </p>
            <a
              href={`/login?redirect=${encodeURIComponent(signInRedirectPath)}`}
              className="btn btn-primary"
              style={{ textAlign: 'center', padding: '0.75rem 1.5rem' }}
            >
              <i className="fas fa-sign-in-alt me-2" aria-hidden></i>
              Sign In to Continue
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (showAlreadyApplied) {
    return (
      <div className="mission-application-form">
        <div className="login-prompt-container">
          <div className="card" style={{ maxWidth: '500px', margin: '0 auto', padding: '2rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>Already Registered</h3>
            <p style={{ marginBottom: '1.5rem', color: '#666' }}>
              A registration for <strong>{formData.email}</strong> has already been submitted for this event.
            </p>
            <button
              type="button"
              onClick={() => setShowAlreadyApplied(false)}
              className="btn btn-outline"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mission-application-form">
      {saveError && (
        <div className="alert alert-danger" role="alert">
          {saveError}
        </div>
      )}

      {!user && (
        <div className="guest-notice mb-4">
          <p className="mb-2">
            Already have an account?{' '}
            <a href={`/login?redirect=${encodeURIComponent(signInRedirectPath)}`}>
              Sign in
            </a>
          </p>
        </div>
      )}

      {signupOptions.length > 0 && (
        <div className="card mb-4" style={{ border: errors.signupOptions ? '2px solid #dc3545' : undefined }}>
          <div className="card-body">
            <h5 className="mb-3">
              {signupOptionMode === 'single' ? 'Select Registration Option *' : 'Select Registration Options *'}
            </h5>
            <div className="row">
              {signupOptions
                .sort((a, b) => a.display_order - b.display_order)
                .map((option) => {
                  const isSelected = selectedSignupOptions.includes(option.id);
                  const optionDates = formatSignupOptionSchedule(option);
                  return (
                    <div key={option.id} className="col-md-6 mb-3">
                      <div
                        className={`border rounded p-3 h-100 ${isSelected ? 'border-primary bg-light' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSignupOptionToggle(option.id)}
                      >
                        <div className="form-check">
                          <input
                            type={signupOptionMode === 'single' ? 'radio' : 'checkbox'}
                            className="form-check-input"
                            name="signupOption"
                            checked={isSelected}
                            onChange={() => handleSignupOptionToggle(option.id)}
                          />
                          <label className="form-check-label" style={{ cursor: 'pointer' }}>
                            <strong>{option.label}</strong>
                          </label>
                        </div>
                        {optionDates && <p className="mb-1 small text-muted">{optionDates}</p>}
                        {hasSignupPrice(option.price) && (
                          <p className="fs-5 fw-bold text-primary mb-0">{formatSignupPrice(option.price)}</p>
                        )}
                        {option.description && (
                          <p className="mb-0 small text-muted mt-2">{option.description}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
            {signupOptionMode === 'multi' && selectedSignupOptions.length > 0 && showSelectedOptionsTotal && (
              <div className="mt-2 text-end">
                <strong>Total: {formatSignupPrice(selectedOptionsTotal)}</strong>
              </div>
            )}
            {errors.signupOptions && (
              <div className="text-danger mt-2">
                <small>{errors.signupOptions}</small>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="form-container">
        <div className="step-content">
          <h3 className="step-title">Contact Information</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>First Name *</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData((p) => ({ ...p, firstName: e.target.value }))}
                className={errors.firstName ? 'error' : ''}
              />
              {errors.firstName && <span className="error-text">{errors.firstName}</span>}
            </div>

            <div className="form-group">
              <label>Last Name *</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData((p) => ({ ...p, lastName: e.target.value }))}
                className={errors.lastName ? 'error' : ''}
              />
              {errors.lastName && <span className="error-text">{errors.lastName}</span>}
            </div>

            <div className="form-group">
              <label>Phone *</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                className={errors.phone ? 'error' : ''}
              />
              {errors.phone && <span className="error-text">{errors.phone}</span>}
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                className={errors.email ? 'error' : ''}
              />
              {errors.email && <span className="error-text">{errors.email}</span>}
            </div>

            <div className="form-group full-width">
              <label>Church Name *</label>
              <ChurchNameInput
                value={formData.churchName}
                onChange={(churchName) => setFormData((p) => ({ ...p, churchName }))}
                className={errors.churchName ? 'error' : ''}
                error={errors.churchName}
                required
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <div className="right-actions">
            <button
              type="button"
              onClick={() => performSave('submit')}
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {!isSubmitting && <i className="fas fa-paper-plane me-2" aria-hidden></i>}
              {isSubmitting ? 'Submitting...' : 'Submit Registration'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MinimalEventApplicationForm;
